import { flow, padStart, range, times } from 'lodash';
import { Component } from 'react';
import { noop } from '../utils/func';
import {
    HourElement,
    HourLabelElement,
    MinutesContainerElement,
    ScrollContainerElement,
    TimeElement,
    TimePickerElement,
} from './style';

interface OwnTimePickerProps {
    getTimeService: $TSFixMeFunction;
    time?: $TSFixMe;
    onTimeChange?: $TSFixMeFunction;
}

type TimePickerState = $TSFixMe;

type TimePickerProps = OwnTimePickerProps & { onTimeChange: () => void };

export class TimePickerDropdown extends Component<TimePickerProps, TimePickerState> {
    static defaultProps = {
        onTimeChange: noop,
    };

    scrollElement: $TSFixMe;
    timeElements: $TSFixMe;
    timeHandlers: $TSFixMe;

    constructor(props: TimePickerProps) {
        super(props);

        const timeService = props.getTimeService();
        const time = props.time || timeService.getDefaultTime();
        const roundedTime = this.getRoundedTime(time);
        this.state = { time: roundedTime };

        this.timeHandlers = {};
        this.timeElements = {};
    }

    componentDidMount() {
        this.scrollToTime(this.state.time);
    }

    UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
        const { time, getTimeService } = nextProps;

        if (time) {
            const roundedTime = this.getRoundedTime(time);
            if (!getTimeService().isTimeEqual(roundedTime, this.state.time)) {
                this.setState({ time: roundedTime });
                this.scrollToTime(roundedTime);
            }
        }
    }

    onScrollContainerRef = (c: $TSFixMe) => (this.scrollElement = c);

    getTimeRefHandler = (timeKey: $TSFixMe) => {
        if (!this.timeHandlers[timeKey]) {
            this.timeHandlers[timeKey] = (c: $TSFixMe) => (this.timeElements[timeKey] = c);
        }

        return this.timeHandlers[timeKey];
    };

    getRoundedTime(time: $TSFixMe) {
        const timeService = this.props.getTimeService();
        const selectedHours = timeService.getHours(time);
        const selectedMinutes = timeService.getMinutes(time);
        const roundedMinutes = roundToMultipleOfFifteen(selectedMinutes);

        return timeService.getTimeFromHoursAndMinutes(selectedHours, roundedMinutes);
    }

    scrollToTime(time: $TSFixMe) {
        const timeKey = this.props.getTimeService().getTimeUniqueName(time);
        const timeElement = this.timeElements[timeKey];

        window.requestAnimationFrame(() => {
            if (timeElement && this.scrollElement) {
                scrollTo(timeElement, this.scrollElement, {
                    topOffset: (this.scrollElement.offsetHeight - timeElement.offsetHeight) / 2,
                });
            }
        });
    }

    handleTimeClick = (time: $TSFixMe) => () => this.props.onTimeChange(time);

    render() {
        const { getTimeService, onTimeChange, ...other } = this.props;
        const { time: roundedTime } = this.state;
        const timeService = getTimeService();

        const has24HourFormat = timeService.has24HourFormat();
        const shouldPadHours = timeService.shouldPadHours();
        const meridiemLabels = timeService.getMeridiemLabels();

        let hours;
        if (has24HourFormat) {
            hours = shouldPadHours ? HOURS_24_FORMAT_PADDED : HOURS_24_FORMAT;
        } else {
            hours = shouldPadHours ? HOURS_12_FORMAT_PADDED : HOURS_12_FORMAT;
        }

        return (
            <TimePickerElement {...other}>
                <ScrollContainerElement ref={this.onScrollContainerRef}>
                    {hours.map((hour) => (
                        <HourElement key={hour.value}>
                            <HourLabelElement as="div" textStyle="bodySmallStrong">
                                <span>
                                    {hour.text}{' '}
                                    {has24HourFormat ? SEPARATOR : meridiemLabels[Math.floor(hour.value / 12)]}
                                </span>
                            </HourLabelElement>
                            <MinutesContainerElement>
                                {MINUTES.map((minute) => {
                                    const time = timeService.getTimeFromHoursAndMinutes(hour.value, minute.value);
                                    const timeKey = timeService.getTimeUniqueName(time);
                                    const selected = roundedTime && timeService.isTimeEqual(time, roundedTime);
                                    const timeShortString = timeService.getShortString(time);

                                    return (
                                        <TimeElement
                                            as="div"
                                            key={timeKey}
                                            ref={this.getTimeRefHandler(timeKey)}
                                            selected={selected}
                                            time={time}
                                            onClick={this.handleTimeClick(time)}
                                            data-test-time={timeShortString}
                                            textStyle="bodySmall"
                                        >
                                            {minute.text}
                                        </TimeElement>
                                    );
                                })}
                            </MinutesContainerElement>
                        </HourElement>
                    ))}
                </ScrollContainerElement>
            </TimePickerElement>
        );
    }
}

const padValue = (value: $TSFixMe) => padStart(value, 2, '0');
const replace12with0 = (value: $TSFixMe) => (value === 0 ? 12 : value);
const getValue =
    (decorate = (v: $TSFixMe) => v) =>
    (value: $TSFixMe) => ({
        value,
        text: decorate(value),
    });
const scrollTo = (
    target: $TSFixMe,
    scrollContainer: $TSFixMe,
    { speed = 500, easing = SCROLL_EASING, callback = () => {}, topOffset = 0 } = {}
) => {
    const startLocation = scrollContainer.scrollTop;
    const endLocation = target.offsetTop - topOffset;
    const distance = Math.abs(endLocation - startLocation);
    const distanceMagnitude = startLocation <= endLocation ? 1 : -1;

    const start = Date.now();
    const stepFunc = () => {
        const timeLapsed = Date.now() - start;
        const percentage = Math.min(timeLapsed / speed, 1);

        const scrolledDistance = distance * easing(percentage) * distanceMagnitude;
        const position = (startLocation as number) + scrolledDistance;

        scrollContainer.scrollTop = position;
        if (position === endLocation) {
            return callback();
        }

        window.requestAnimationFrame(stepFunc);
    };

    window.requestAnimationFrame(stepFunc);
};

// acceleration until halfway, then deceleration
const SCROLL_EASING = (time: $TSFixMe) =>
    time < 0.5 ? 4 * time ** 3 : (time - 1) * (2 * time - 2) * (2 * time - 2) + 1;

const HOURS_12_FORMAT = times(24, getValue(flow((v: $TSFixMe) => v % 12, replace12with0)));
const HOURS_12_FORMAT_PADDED = times(24, getValue(flow((v: $TSFixMe) => v % 12, replace12with0, padValue)));
const HOURS_24_FORMAT = times(24, getValue());
const HOURS_24_FORMAT_PADDED = times(24, getValue(padValue));

const MINUTES = range(0, 60, 15).map(getValue(padValue));
const SEPARATOR = ':';

function roundToMultipleOfFifteen(value: $TSFixMe) {
    return Math.floor(value / 15) * 15;
}
